import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowRight, Rocket, Store, ShoppingBag, Cpu, MapPin, Check } from "lucide-react";
import Breadcrumb from "../components/Breadcrumb";
import SectionLabel from "../components/SectionLabel";
import ScrollReveal from "../components/ScrollReveal";
import { INDUSTRIES_DETAIL, PROJECTS, SERVICES } from "../lib/data";

const ICONS = { Rocket, Store, ShoppingBag, Cpu, MapPin };

export default function IndustryDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const ind = INDUSTRIES_DETAIL.find((x) => x.slug === slug);

  if (!ind) {
    return (
      <div className="page container-x" style={{ padding: "180px 0" }}>
        <h1>Industry not found</h1>
        <button className="btn btn-primary" onClick={() => navigate("/industries")} style={{ marginTop: 24 }}>Back to industries</button>
      </div>
    );
  }

  const Ic = ICONS[ind.icon] || Rocket;
  const samples = PROJECTS.filter((p) => ind.samples.includes(p.slug));
  const services = SERVICES.filter((s) => ind.services.some((x) => x.toLowerCase().includes(s.title.toLowerCase().split(" ")[0])));

  return (
    <div className="page" data-testid="industry-detail-page">
      <section className="page-hero">
        <div className="container-x">
          <Breadcrumb items={[
            { label: "Home", to: "/" },
            { label: "Industries", to: "/industries" },
            { label: ind.title, to: `/industries/${ind.slug}` },
          ]} />
          <div style={{ width: 64, height: 64, borderRadius: 16, background: "var(--carbon-black)", color: "var(--bright-snow)", display: "inline-flex", alignItems: "center", justifyContent: "center", marginTop: 24 }}>
            <Ic size={28} />
          </div>
          <h1 style={{ marginTop: 24 }}>{ind.title}.</h1>
          <p className="lead">{ind.short}</p>
        </div>
      </section>

      <section className="section section-bright">
        <div className="container-x">
          <SectionLabel num={1}>Why It Matters</SectionLabel>
          <h2 className="sec-title">How Sinix Studio helps {ind.title.toLowerCase()}.</h2>
          <div className="card" style={{ marginTop: 32, maxWidth: 880 }}>
            <p style={{ color: "var(--iron-grey)", fontSize: 17, lineHeight: 1.7 }}>{ind.why}</p>
          </div>
        </div>
      </section>

      <section className="section section-platinum">
        <div className="container-x">
          <SectionLabel num={2}>Relevant Services</SectionLabel>
          <h2 className="sec-title">What you'll typically need.</h2>
          <div className="deliv-grid">
            {ind.services.map((s) => (
              <div key={s} className="deliv-card">
                <div className="check"><Check size={16} /></div>
                <span>{s}</span>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 36, display: "flex", gap: 12, flexWrap: "wrap" }}>
            {services.slice(0, 3).map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} className="btn btn-outline">
                {s.title} <ArrowRight size={14} />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {samples.length > 0 && (
        <section className="section section-bright">
          <div className="container-x">
            <SectionLabel num={3}>Related Work</SectionLabel>
            <h2 className="sec-title">Projects in this space.</h2>
            <div className="work-grid">
              {samples.map((p, i) => (
                <ScrollReveal key={p.slug} delay={i * 80}>
                  <Link to={`/work/${p.slug}`} className="proj-card">
                    <div className="proj-img"><img src={p.cover} alt={p.name} loading="lazy" /></div>
                    <div className="proj-body">
                      <div className="proj-meta-row">
                        {p.tags.map((t) => <span key={t} className="pill">{t}</span>)}
                      </div>
                      <div className="proj-byline">{p.client} · {p.year}</div>
                      <h3>{p.name}</h3>
                      <p>{p.short}</p>
                      <span className="arrow-link">View Project <ArrowRight size={14} /></span>
                    </div>
                  </Link>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <div className="container-x">
        <div className="cta-dark">
          <div className="lead">Industry-specific help</div>
          <h2>Ready to make {ind.title.toLowerCase()} look unmistakable?</h2>
          <Link to="/contact" className="btn btn-light">Get In Touch <ArrowRight size={16} /></Link>
        </div>
      </div>
    </div>
  );
}
